/* Created by Interactive 63 - https://interactive63.com */
/**
 * The shared services the inspector's parts need: the store, playback, the stage and helpers
 * for reading values at the playhead.
 */
import { sampleTrack } from '../../../core/timeline/sample';
import { findTrack } from '../../../core/timeline/keyframes';
import type { ElementId, PropertyKey, PropertyValue } from '../../../core/model/types';
import { cascadeTargets } from '../../../core/properties/cascade';
import { PROPERTIES } from '../../../core/properties/registry';
import type { PlaybackController } from '../../playback';
import type { Store } from '../../store/store';
import { documentColours } from './document-colours';
import type { StagePanel } from '../stage/stage';
import { originalPathData } from '../../../core/morph/convert';
import { ShapeTools } from '../../morph/shape-tools';

const SVG_NS = 'http://www.w3.org/2000/svg';

export interface InspectorContext {
  store: Store;
  playback: PlaybackController;
  /** The playhead time: live while playing, otherwise the stored playhead. */
  time: () => number;
  isPlaying: () => boolean;
  /** The value an element shows for a property at the playhead. */
  readValue: (elementId: ElementId, property: PropertyKey) => PropertyValue;
  documentColours: () => string[];
  /** Shape morphing actions. */
  shapes: ShapeTools;
}

/** Creates the context used by every inspector part. */
export function createInspectorContext(
  store: Store,
  playback: PlaybackController,
  stage: StagePanel,
): InspectorContext {
  /** The stored playhead, or the live time while playing. */
  const time = (): number =>
    playback.isPlaying ? playback.currentTime : store.getState().editor.playhead;

  const shapes = new ShapeTools(store, stage, time);
  const ctx: InspectorContext = {
    store,
    playback,
    shapes,
    time,
    isPlaying: () => playback.isPlaying,
    readValue: (elementId, property) => {
      const { project } = store.getState();
      // A group shows the value of the first shape inside it, since edits are written there.
      const [shown = elementId] = cascadeTargets(project, [elementId], property);
      if (shown !== elementId) return ctx.readValue(shown, property);
      const track = findTrack(project, elementId, property);
      if (track?.enabled) {
        const sampled = sampleTrack(track, time(), project.scene.duration);
        if (sampled !== undefined) return sampled;
      }
      if (property === 'morph') {
        // The stored shape, because the stage may be showing a point partway through a morph.
        const d = originalPathData(project.svg.source, elementId);
        if (d !== '') return { d };
      }
      const rendered = stage.getElement(elementId);
      const tag = project.elements[elementId]?.tag ?? 'g';
      const element = rendered ?? document.createElementNS(SVG_NS, tag);
      return PROPERTIES[property].defaultValue(element as SVGElement);
    },
    documentColours: () => documentColours(store.getState().project.svg.source),
  };
  return ctx;
}
